import { defineStore } from "pinia";
import { searchPosts } from "@/fetch";
import { months } from "@/locales";

export type Term = {
  id: number;
  slug: string;
  name: string;
  order: number;
  children: Array<Term>;
};
export type Year = { year: number; months: Array<number> };
export type Image = {
  alt: string;
  medium: string;
  large: string;
  style: string;
};
export type Item = {
  id: number;
  lang: string;
  title: string;
  date: string;
  link: string;
  image: Image;
  primary: Array<Term>;
  secondary: Array<Term>;
  search: Array<string>;
  order: number;
};

export interface State {
  type: string;
  lang: string;
  items: Array<Item>;
  show: number;
  page: number;
  year: number;
  month: number;
  title: string;
  tag: string;
  mustHave: string;
  filter: {
    primary: Array<string>;
    secondary: Array<string>;
  };
  historyInhibit: boolean;
  taxonomy: {
    primary: string;
    secondary: string;
  };
  terms: {
    primary: Array<Term>;
    secondary: Array<Term>;
  };
  months: Array<Year>;
  search: string;
  asc: boolean;
  sort: boolean;
  sortType: OrderByType;
  showSort: boolean;
  showDate: boolean;
  showMonth: boolean;
  sortButtonText: string;
  clicks: number;
}
export type OrderByType = "post_title" | "post_date" | "menu_order";
export const useItemStore = defineStore("items", {
  state: (): State => ({
    type: "",
    lang: "",
    items: [] as Array<Item>,
    show: 9,
    page: 1,
    year: 0,
    month: 0,
    title: "",
    tag: "",
    mustHave: "",
    filter: {
      primary: [] as Array<string>,
      secondary: [] as Array<string>,
    },
    historyInhibit: false,
    taxonomy: {
      primary: "",
      secondary: "",
    },
    terms: {
      primary: [] as Array<Term>,
      secondary: [] as Array<Term>,
    },
    months: [] as Array<Year>,
    search: "",
    asc: true,
    sort: true,
    sortType: "post_title",
    showSort: true,
    showDate: false,
    showMonth: false,
    sortButtonText: "",
    clicks: 0,
  }),
  getters: {
    /**
     * Filters out items according to given criteria.
     * @returns {Array<Item>}
     */
    filterItems: function (state): Array<Item> {
      return state.items.filter((item) => {
        if (item.lang !== state.lang) {
          return false;
        }
        if (state.filter.primary.length > 0) {
          let hit = false;
          item.primary.forEach((term) => {
            if (state.filter.primary.includes(term.slug)) hit = true;
          });
          if (!hit) return false;
        }
        if (state.filter.secondary.length > 0) {
          let hit = false;
          item.secondary.forEach((term) => {
            if (state.filter.secondary.includes(term.slug)) hit = true;
          });
          if (!hit) return false;
        }
        if (state.mustHave.length > 0) {
          let hit = false;
          item.secondary.forEach((term) => {
            if (state.mustHave === term.slug) hit = true;
          });
          if (!hit) return false;
        }

        if (state.search.length > 0) {
          return (
            item.title.toLowerCase().includes(state.search.toLowerCase()) ||
            item.search.includes(state.search.toLowerCase())
          );
        }
        if (state.year > 0) {
          const date = new Date(item.date);
          if (date.getFullYear() != state.year) {
            return false;
          }
          if (state.month > 0) {
            if (date.getMonth() + 1 != state.month) {
              return false;
            }
          }
          console.debug(date);
        }
        return true;
      });
    },
    /**
     * Sort and limit the filtered list.
     * @returns {Item[]}
     */
    getItems: function (state): Array<Item> {
      return this.filterItems
        .toSorted((a, b) => {
          switch (this.getOrderBy) {
            case "post_title":
              if (a.title === b.title) return 0;
              if (a.title > b.title) return state.asc ? 1 : -1;
              return state.asc ? -1 : 1;
            case "post_date":
              if (a.date === b.date) return 0;
              if (a.date > b.date) return state.asc ? 1 : -1;
              return state.asc ? -1 : 1;
            case "menu_order":
              return a.order - b.order;
            default:
              if (state.sort) {
                if (a.title === b.title) return 0;
                if (a.title > b.title) return state.asc ? 1 : -1;
                return state.asc ? -1 : 1;
              } else {
                if (a.date === b.date) return 0;
                if (a.date > b.date) return -1;
                return 1;
              }
          }
        })
        .slice(0, state.show);
    },
    getPrimary: function (state): Array<Term> {
      return sortTerms(state.terms.primary);
    },
    getSecondary: function (state): Array<Term> {
      return sortTerms(state.terms.secondary);
    },
    /**
     * Check if there are more items to show.
     * @returns {boolean}
     */
    hasMore: function (state): boolean {
      return this.filterItems.length > state.show;
    },
    getMonth: function (state): string {
      return months(state.lang)[state.month];
    },
    getMonths: function (state): Array<number> {
      if (state.year == 0) {
        return [];
      }
      for (let year of state.months) {
        if (state.year == year.year) {
          return year.months;
        }
      }
      return [];
    },
    getOrderBy: function (state): OrderByType {
      if (state.sortType === "menu_order" && state.clicks > 0) {
        return "post_title";
      }
      if (state.showDate && !state.sort && state.sortType === "post_title") {
        return "post_date";
      }
      return state.sortType;
    },
  },
  actions: {
    addItem(item: Item) {
      let found = false;
      this.items.forEach((i) => {
        if (i.id === item.id) {
          found = true;
          if (item.search.length > 0) {
            i.search.push(...item.search);
          }
        }
      });
      if (!found) {
        // Add item if not in store.
        this.items.push(item);
      }
    },
    addCats(
      cats: Array<{
        id: number;
        slug: string;
        name: string;
        order: number;
        parent: number;
      }>,
      cat: string,
    ) {
      const categories =
        this.taxonomy.primary === cat
          ? this.terms.primary
          : this.terms.secondary;

      for (let cat of cats) {
        if (cat.parent == 0) {
          const category = {
            id: cat.id,
            slug: cat.slug,
            name: cat.name,
            order: cat.order,
            children: [],
          } as Term;
          categories.push(category);
        }
      }
      for (let cat of cats) {
        if (cat.parent > 0) {
          const category = {
            id: cat.id,
            slug: cat.slug,
            name: cat.name,
            order: cat.order,
            children: [],
          } as Term;
          categories.forEach((c) => {
            if (c.id == cat.parent) {
              c.children.push(category);
            }
          });
        }
      }
    },
    toggleSort() {
      if (this.sortType === "menu_order") {
        switch (this.clicks) {
          case 0:
            this.clicks++;
            this.sort = true;
            this.asc = true;
            break;
          case 1:
            this.clicks++;
            this.asc = false;
            break;
          case 2:
            this.clicks = 0;
            this.sort = false;
            this.asc = false;
            break;
          default:
            this.clicks = 0;
            break;
        }
      } else if (this.showDate && this.sort && !this.asc) {
        // For date types, toggle sorting off after desc.
        this.sort = false;
      } else {
        this.sort = true;
        this.asc = !this.asc;
      }
      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    setFilter(taxonomy: string, slug: string) {
      const primary = taxonomy === this.taxonomy.primary;
      const filter = primary ? this.filter.primary : this.filter.secondary;
      if (filter.includes(slug)) {
        // Remove the item.
        for (let i = 0; i < filter.length; i++) {
          if (filter[i] === slug) {
            filter.splice(i, 1);
            break;
          }
        }
      } else {
        // Add the item.
        filter.push(slug);
      }

      this.historyInhibit = true;
      const hashSource = [
        this.filter.primary.join(","),
        this.filter.secondary.join(","),
      ];
      window.location.hash = hashSource.join("/");
      const uri = window.location.toString();
      if (
        this.filter.primary.length + this.filter.secondary.length === 0 &&
        uri.indexOf("#") > 0
      ) {
        window.history.replaceState(
          {},
          document.title,
          uri.substring(0, uri.indexOf("#")),
        );
      }

      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    setFilters(termsString: string, primary: boolean) {
      const terms = termsString.split(",").filter((term) => {
        return term !== "";
      });
      if (primary) {
        this.filter.primary = terms;
      } else {
        this.filter.secondary = terms;
      }

      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    setYear(year: number) {
      if (this.year != year) {
        this.month = 0;
      }
      this.year = year;
      searchPosts();
    },
    setMonth(month: number) {
      this.month = month;
      searchPosts();
    },
    showMore() {
      this.show += 8;
      if (this.filterItems.length - this.show < 8 && this.page > 0) {
        this.page++;
        searchPosts();
      }
    },
    resetShow() {
      this.page = 1;
      this.show = 9;
    },
  },
});

const sortTerms = (terms: Array<Term>): Array<Term> => {
  return terms
    .filter((term: Term) => {
      return term.order > -1;
    })
    .sort((a: Term, b: Term) => {
      return a.order - b.order;
    });
};
